// Build 96.2 — Delad logik för "Kartmeny" (konsoliderat publikt filter).
// Används av både publika KartaSida och admin-sammelvyn `Platser`.
//
// Backend speglar listan i KARTMENY_KEYS i server.py — håll listorna i sync.

const normTag = (t) => String(t || "").toLowerCase().trim();

export const FILTER_GROUPS = [
  {
    key: "folk_foreningar",
    label: "Folk & föreningar",
    sources: new Set(["forening"]),
    cats: new Set(["Förening"]),
    tags: new Set([
      "förening", "bygdegård", "bygdegårdar", "samlingslokal", "samlingslokaler",
      "idrottsförening", "idrottsföreningar", "lokalt engagemang",
      "mötesplats", "mötesplatser",
    ]),
  },
  {
    key: "friluft_idrott",
    label: "Friluft & idrott",
    cats: new Set([
      "Friluftsliv", "Friluftsområde", "Vandring", "Natur",
      "Vindskydd", "Grillplats", "Rastplats", "Utsiktsplats",
      "Badplats", "Båtramp", "Fiskevatten", "Lekplats",
      "Bärställe", "Svampställe",
    ]),
    tags: new Set([
      "vandring", "vandra", "led", "stig", "leder",
      "skidor", "skidåkning", "elljusspår",
      "cykel", "bike", "cykling",
      "bad", "fiske", "båt",
      "sport", "idrott", "motion",
      "hundspann", "sportplats", "sportplatser",
      "naturupplevelse", "naturupplevelser",
    ]),
  },
  {
    key: "hemma_byarna",
    label: "Hemma i byarna",
    sources: new Set(["area"]),
    cats: new Set(["By", "Socken", "Ort", "Bygd"]),
    tags: new Set([
      "by", "bydel", "bydelar", "småort", "småorter",
      "landskapspunkt", "landskapspunkter",
      "lokala namn", "gårdsbygd", "gårdsbygder", "lokal identitet",
    ]),
  },
  {
    key: "loppis_fika",
    label: "Loppis & fika",
    cats: new Set(["Loppis", "Café", "Sommarcafé"]),
    tags: new Set([
      "loppis", "flohmarkt", "gårdsförsäljning",
      "café", "kafé", "fika", "sommarcafé",
      "mat", "matställe", "restaurang",
      "marknad", "marknadsdag",
      "lätt besöksmål", "lätta besöksmål",
    ]),
  },
  {
    key: "praktisk_hjalp",
    label: "Praktisk hjälp",
    sources: new Set(["foretag"]),
    cats: new Set([
      "Service", "Företag", "Lantbruk",
      "Hjärtstartare", "Återvinning", "Offentlig toalett",
    ]),
    tags: new Set([
      "service", "verkstad",
      "flygfält", "flygplats",
      "lokal resurs", "lokala resurser", "hjälp",
    ]),
  },
  {
    key: "utflykter_historia",
    label: "Utflykter & historia",
    cats: new Set([
      "Sevärdhet", "Kulturminne", "Historisk plats",
      "Naturtips", "Favoritplats",
    ]),
    tags: new Set([
      "utflykt", "utflyktsmål", "sevärdhet", "sevärdheter",
      "historia", "kultur", "kulturminne", "kulturhistoria",
      "kyrka", "hembygd", "stenålder",
      "kraftverk", "damm", "dammar",
      "gamla vägar", "gammal väg",
      "mystisk plats", "mystiska platser",
      "utsiktsplats", "utsiktsplatser",
    ]),
  },
  {
    key: "vardag",
    label: "Vardag",
    sources: new Set(["vardagskort"]),
    cats: new Set(["Vardag"]),
    tags: new Set([
      "butik", "store", "ica", "post", "ombud", "apotek",
      "buss", "transport",
    ]),
  },
  {
    key: "visa_allt",
    label: "Visa allt",
    all: true,
  },
];

// Endast 7 "riktiga" keys får sparas i DB. Visa allt är UI-reset.
export const KARTMENY_STORABLE_KEYS = new Set(
  FILTER_GROUPS.filter((g) => !g.all).map((g) => g.key)
);

// {key: label} — för dropdowns och visning av fallback
export const KARTMENY_LABEL = Object.fromEntries(
  FILTER_GROUPS.filter((g) => !g.all).map((g) => [g.key, g.label])
);

export function matchesFilterGroup(item, group) {
  if (!group || group.all) return true;
  // Prioritet 1: explicit kartmeny från moderator
  if (item.kartmeny && KARTMENY_STORABLE_KEYS.has(item.kartmeny)) {
    return item.kartmeny === group.key;
  }
  // Fallback för gamla poster utan kartmeny: source + kategori + tag-heuristik
  if (group.sources && group.sources.has(item.source)) return true;
  if (group.cats && item.kategori && group.cats.has(item.kategori)) return true;
  if (group.tags && Array.isArray(item.taggar)) {
    for (const t of item.taggar) {
      if (group.tags.has(normTag(t))) return true;
    }
  }
  return false;
}

/**
 * Returnerar fallback-kartmeny-nyckeln för en post UTAN explicit kartmeny.
 * Används av admin-sammelvyn för att förklara "Visas via fallback: …".
 * Om ingen grupp matchar returneras null (posten hamnar i tysta resten).
 */
export function getFallbackKartmeny(item) {
  if (!item || typeof item !== "object") return null;
  if (item.kartmeny && KARTMENY_STORABLE_KEYS.has(item.kartmeny)) {
    // Redan satt → ingen fallback behövs (men returnera ändå för konsekvens).
    return null;
  }
  for (const g of FILTER_GROUPS) {
    if (g.all) continue;
    if (g.sources && g.sources.has(item.source)) return g.key;
    if (g.cats && item.kategori && g.cats.has(item.kategori)) return g.key;
    if (g.tags && Array.isArray(item.taggar)) {
      for (const t of item.taggar) {
        if (g.tags.has(normTag(t))) return g.key;
      }
    }
  }
  return null;
}
